import type { Locale } from "./config";

const en = {
  common: {
    appName: "HireSensor",
    signIn: "Sign in",
    signUp: "Sign up",
    signOut: "Sign out",
    getStarted: "Get started",
    back: "Back",
    next: "Next",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    copy: "Copy",
    copied: "Copied",
    required: "Required",
    skip: "Skip",
    finish: "Finish",
    generalInterview: "General interview",
    candidate: "Candidate",
    language: "Language",
  },
  nav: {
    dashboard: "Dashboard",
    jobs: "Jobs",
    candidates: "Candidates",
    interviews: "Interviews",
    partners: "Partners",
    reports: "Admin",
    careers: "Careers",
    contact: "Contact us",
    myInterviews: "My interviews",
    myCv: "My CV",
    talent: "Talent",
  },
  landing: {
    badge: "Interview smarter with AI",
    heroTitle: "Hire the right people with",
    heroHighlight: "AI-led interviews",
    heroSubtitle:
      "Generate job descriptions, invite candidates, and run secure, monitored AI interviews that are automatically scored — so you focus only on the best.",
    ctaStart: "Start hiring free",
    ctaHaveAccount: "I have an account",
    testimonialsTitle: "What hiring teams say",
    testimonials: [
      {
        role: "Hiring Manager",
        quote:
          "We went from scheduling rounds of screens to reviewing scored interviews the same day. The shortlist feels sharper and fairer.",
      },
      {
        role: "Talent Lead",
        quote:
          "Structured questions grounded in the role and CV keep every candidate on equal footing. Our team spends time only where it matters.",
      },
      {
        role: "Recruiting Agency Partner",
        quote:
          "Clients get clear recommendations with evidence, not gut feel. It has become our default first interview step.",
      },
    ],
    featuresTitle: "Everything you need to screen faster",
    featuresSubtitle: "A complete, fair, and fast interview pipeline powered by AI.",
    howTitle: "How it works",
    howSubtitle: "From open role to decision in four steps.",
    ctaBannerTitle: "Ready to interview smarter?",
    ctaBannerSubtitle: "Set up your first AI interview in minutes.",
    footer: "Built for modern hiring teams.",
    features: [
      { title: "AI job descriptions", desc: "Answer a short wizard and get a polished, inclusive job description in seconds." },
      { title: "Monitored interviews", desc: "Camera, voice identity check, and focus tracking keep every interview fair." },
      { title: "Anti-cheat proctoring", desc: "We detect tab switching and flag suspicious behavior automatically." },
      { title: "Timed & structured", desc: "Wizard-based questions with per-question timers and a 30-minute cap." },
      { title: "AI evaluation", desc: "Every answer is scored with strengths, concerns, and a hire recommendation." },
      { title: "Live oversight", desc: "Watch sessions live and get notified when candidates start or finish." },
    ],
    steps: [
      { title: "Create the role", desc: "Use the AI wizard to generate the job description." },
      { title: "Invite the candidate", desc: "Upload their CV and send a one-click interview link." },
      { title: "AI interviews them", desc: "The candidate completes a monitored, timed interview grounded in the role and CV." },
      { title: "Review results", desc: "Get scores, recordings, and a clear recommendation." },
    ],
  },
  auth: {
    welcomeBack: "Welcome back",
    signInSubtitle: "Sign in to continue.",
    createTitle: "Create your account",
    createSubtitle: "Choose how you want to use HireSensor.",
    email: "Email",
    password: "Password",
    fullName: "Full name",
    country: "Country / region",
    countryPh: "Select your country",
    company: "Company",
    workEmail: "Work email",
    createAccount: "Create account",
    noAccount: "No account?",
    alreadyHave: "Already have an account?",
    otpTitle: "Check your email",
    otpSubtitle: "We sent a 6-digit code to {email}.",
    otpLabel: "Verification code",
    otpVerify: "Verify and sign in",
    otpResend: "Resend code",
    otpBack: "Back to sign in",
    otpInvalid: "Invalid or expired code.",
    otpResent: "A new code was sent to your email.",
    otpResendFailed: "Could not resend the code.",
    otpDevHint: "Couldn't send email right now. Your verification code is {code}",
    otpEmailFailed:
      "We couldn't send the verification email. Wait a moment, then tap Resend code.",
    otpSent: "Code sent to your email.",
    agencyToggle: "I'm a recruiting agency",
    roleHire: "I want to hire",
    roleHireDesc: "Company workspace for jobs and interviews",
    roleSeek: "I am looking for a job",
    roleSeekDesc: "Browse public roles and apply",
    staySignedIn: "Stay signed in",
    emailNotRegistered:
      "No account found for this email. Please sign up first.",
    forgotPassword: "Forgot password?",
    forgotTitle: "Reset your password",
    forgotSubtitle:
      "Enter your email and we'll send a link to reset your password.",
    forgotSubmit: "Send reset link",
    forgotSuccess:
      "If an account exists for that email, we've sent a reset link. Check your inbox.",
    forgotDevHint: "Couldn't send email right now. Use this reset link: {url}",
    forgotBack: "Back to sign in",
    resetTitle: "Choose a new password",
    resetSubtitle: "Enter a new password for your account.",
    resetPassword: "New password",
    resetConfirm: "Confirm password",
    resetSubmit: "Update password",
    resetSuccess: "Password updated. You can sign in now.",
    resetMismatch: "Passwords do not match.",
    resetTooShort: "Password must be at least 8 characters.",
    resetInvalid:
      "This reset link is invalid or has expired. Request a new one.",
    resetMissingToken: "Missing reset token. Open the link from your email.",
    cvOptional: "CV (optional)",
    cvHint: "PDF only, up to 5 MB. Companies can discover you if you make it public.",
    cvVisibility: "CV visibility",
    cvPrivate: "Private — only you",
    cvPublic: "Public — visible to companies & agencies",
  },
  profile: {
    title: "My CV",
    subtitle: "Upload your CV and choose who can see it.",
    upload: "Upload CV (PDF)",
    replace: "Replace CV",
    current: "Current CV",
    download: "Download CV",
    noCv: "No CV uploaded yet.",
    visibility: "Visibility",
    private: "Private — hidden from companies",
    public: "Public — companies and agencies can find you",
    save: "Save changes",
    saved: "Profile saved.",
    saveError: "Could not save profile.",
    loadError: "Could not load profile.",
    pdfOnly: "Please upload a PDF file (max 5 MB).",
  },
  talent: {
    title: "Talent",
    subtitle: "Browse candidates who made their CV public.",
    search: "Search by name or email",
    empty: "No public profiles yet.",
    emptyDesc: "When candidates publish their CV, they will appear here.",
    view: "View",
    hasCv: "CV available",
    noCv: "No CV file",
    updated: "Updated",
    back: "Back to talent",
    detailTitle: "Candidate profile",
    invite: "Invite to interview",
    loadError: "Failed to load talent profiles.",
    notFound: "Profile not found or is private.",
  },
  dashboard: {
    welcome: "Welcome",
    subtitle: "Here's your hiring pipeline at a glance.",
    newJob: "New job",
    inviteCandidate: "Invite candidate",
    statJobs: "Jobs",
    statCandidates: "Candidates",
    statInterviews: "Interviews",
    statCompleted: "Completed",
    recentInterviews: "Recent interviews",
    results: "Results",
    viewAll: "View all",
    noInterviews: "No interviews yet.",
    noMatches: "No interviews match your filters.",
    inviteFirst: "Invite your first candidate",
  },
  filters: {
    allJobs: "All jobs",
    generalOnly: "General (no role)",
    allStatuses: "All statuses",
    allDecisions: "All decisions",
    from: "From",
    to: "To",
    clear: "Clear",
    searchPlaceholder: "Search by name, email, or role",
  },
  jobs: {
    title: "Jobs",
    subtitle: "Roles you're hiring for.",
    newJob: "New job",
    empty: "No jobs yet",
    emptyDesc: "Create your first AI-generated job description.",
    createdPrefix: "Created",
    newTitle: "Create a job with AI",
    newSubtitle: "Answer a few questions and we'll write the description for you.",
    backToJobs: "Back to jobs",
    wizardSteps: [
      "Basics",
      "Responsibilities",
      "Skills",
      "Compensation",
      "Listing",
    ],
    jobTitle: "Job title",
    jobTitlePh: "e.g. Senior Frontend Engineer",
    seniority: "Seniority",
    questionDifficulty: "Interview difficulty",
    questionDifficultyHint:
      "Simple: 20 questions · Medium: 40 questions · Advanced: 60 questions",
    difficultyEasy: "Simple (20 questions)",
    difficultyMedium: "Medium (40 questions)",
    difficultyHard: "Advanced (60 questions)",
    employmentType: "Employment type",
    location: "Location",
    locationPh: "Type Remote, Hybrid, or a city…",
    locationNoMatches: "No matching locations",
    locationPickHint: "Pick a location from the suggestions.",
    locationHybridCity: "Office location",
    locationHybridCityPh: "Type a city for hybrid…",
    locationHybridHint: "Hybrid needs an office city from the list.",
    responsibilities: "Key responsibilities",
    responsibilitiesPh: "What will this person do day to day? Bullet points are fine.",
    mustHave: "Must-have skills",
    mustHavePh: "e.g. React, TypeScript, 5+ years experience",
    niceToHave: "Nice-to-have skills",
    niceToHavePh: "e.g. Next.js, design systems, GraphQL",
    salaryRange: "Salary range (optional)",
    salaryRangePh: "e.g. 120k–180k SAR",
    anythingElse: "Anything else?",
    anythingElsePh: "Team, mission, benefits, working style...",
    listingEndDate: "Listing end date",
    listingEndDateHint: "Required. Job leaves Careers after this date.",
    listingVisibility: "Visibility",
    visibilityPrivate: "Private — invite only",
    visibilityPublic: "Public — listed on Careers",
    listingExpires: "Expires {date}",
    listingExpired: "Expired",
    badgePublic: "Public",
    badgePrivate: "Private",
    generate: "Generate with AI",
    inviteCandidate: "Invite candidate",
    deleteConfirm: "Delete this job?",
    noDescription: "No description",
    genError: "AI generation failed. Please try again in a moment.",
    active: "Active",
    colTitle: "Role",
    colCandidates: "Candidates",
    colCompleted: "Completed",
    colCreated: "Created",
    colActions: "Actions",
    view: "View",
    edit: "Edit",
    viewCandidates: "View candidates",
    delete: "Delete",
    noMatches: "No jobs match your filters.",
  },
  careers: {
    title: "Careers",
    subtitle: "Open roles hiring with HireSensor.",
    empty: "No public roles right now.",
    emptyDesc: "Check back later, or sign up to apply when roles appear.",
    company: "Company",
    location: "Location",
    expires: "Apply by {date}",
    apply: "Apply",
    applying: "Applying…",
    applySuccess: "Application submitted — starting your interview.",
    signInToApply: "Sign in as a candidate to apply",
    signUpToApply: "Create a candidate account",
    cvOptional: "CV (PDF, optional, max 5 MB)",
    backToCareers: "Back to careers",
    myInterviewsTitle: "My interviews",
    myInterviewsSubtitle: "Interviews from jobs you applied to.",
    myInterviewsEmpty: "No interviews yet.",
    openInterview: "Open interview",
    remote: "Location not specified",
    searchPlaceholder: "Search by title, company, or location",
    locationPlaceholder: "Location",
    allSeniority: "All seniority",
    allEmployment: "All employment types",
    allSalary: "All salary ranges",
    salaryBuckets: {
      under_72: "Under SAR 72k",
      "72_120": "SAR 72k – 120k",
      "120_180": "SAR 120k – 180k",
      "180_240": "SAR 180k – 240k",
      "240_360": "SAR 240k – 360k",
      "360_480": "SAR 360k – 480k",
      "480_plus": "SAR 480k+",
    },
    sortBy: "Sort by",
    sortNewest: "Newest first",
    sortOldest: "Oldest first",
    sortTitleAsc: "Title A–Z",
    sortTitleDesc: "Title Z–A",
    sortCompanyAsc: "Company A–Z",
    sortExpiresSoon: "Expiring soon",
    noMatches: "No jobs match your filters.",
    noMatchesDesc: "Try clearing filters or broadening your search.",
    clearFilters: "Clear filters",
  },
  candidates: {
    title: "Candidates",
    subtitle: "People invited to interview.",
    inviteCandidate: "Invite candidate",
    empty: "No candidates yet",
    noMatches: "No candidates match your filters.",
    noInterview: "Not interviewed yet",
    latestInterview: "Latest interview",
    colCandidate: "Candidate",
    colJob: "Role",
    colScore: "Score",
    colStatus: "Status",
    colDecision: "Decision",
    colInvited: "Invited",
    colActions: "Actions",
    view: "View",
    newTitle: "Invite a candidate",
    newSubtitle: "Upload their CV and we'll email them an interview link.",
    candidateName: "Candidate name",
    candidateNamePh: "Jane Doe",
    candidateEmail: "Candidate email",
    candidateEmailPh: "jane@example.com",
    jobOptional: "Job (optional)",
    generalOption: "General interview (no specific role)",
    interviewLanguage: "Interview language",
    interviewTime: "Interview time (optional)",
    cvLabel: "Candidate CV (PDF, optional)",
    cvUpload: "Click to upload a PDF CV",
    sendInvitation: "Send invitation",
    invitedTitle: "Candidate invited",
    emailedMsg: "An invitation email has been sent with the interview link.",
    notEmailedMsg: "Email delivery is not configured — share this link with the candidate:",
    viewInterview: "View interview",
    inviteAnother: "Invite another",
  },
  interviews: {
    title: "Interviews",
    subtitle: "Track and review every candidate interview.",
    empty: "No interviews yet",
    emptyDesc: "Invite a candidate to get started.",
    noMatches: "No interviews match your filters.",
    inviteCandidate: "Invite candidate",
    backToInterviews: "Back to interviews",
    waitingTitle: "Waiting for the candidate to start",
    waitingScheduled: "Scheduled for",
    waitingNoSchedule: "No scheduled time set.",
    interviewLink: "Interview link",
    searchPlaceholder: "Search by name, email, or role",
    allStatuses: "All statuses",
    allDecisions: "All decisions",
    decisionTitle: "Hiring decision",
    decisionHint: "Update the candidate's progress.",
  },
  decision: {
    none: "No decision",
    shortlisted: "Shortlisted",
    accepted: "Accepted",
    rejected: "Rejected",
  } as Record<string, string>,
  status: {
    invited: "Invited",
    in_progress: "In progress",
    completed: "Completed",
    violated: "Flagged",
    expired: "Expired",
  },
  live: {
    liveCamera: "Live camera",
    answered: "answered",
    waitingCamera: "Waiting for camera...",
    evaluating: "Interview finished — generating the report…",
    live: "LIVE",
    violations: "focus violation(s) detected.",
    activity: "Activity",
    noActivity: "No activity yet.",
    events: {
      join: "Joined the interview",
      answer: "Answered a question",
      violation: "Left the interview page",
      snapshot: "Camera snapshot",
      voice: "Voice sample recorded",
      finish: "Finished the interview",
      blur: "Window lost focus",
    } as Record<string, string>,
  },
  results: {
    summaryNone: "No summary available.",
    evaluating: "Evaluation in progress — this page will update automatically when the report is ready.",
    strengths: "Strengths",
    concerns: "Concerns",
    noneNoted: "None noted.",
    identityVerification: "Identity verification",
    voiceSample: "Intro recording",
    noVoice: "No intro recording.",
    answersScores: "Answers & scores",
    answerLabel: "Answer:",
    skipped: "Skipped",
    noAnswer: "No answer",
    monitoringSnapshots: "Monitoring snapshots",
    integrityTitle: "Tab monitoring",
    integrityLeft: "Left the interview tab",
    integrityTimes: "{count} time(s)",
    integrityNone: "No tab-leave events recorded.",
    integrityShots: "Screenshots when they left",
    integrityNoShots: "No leave screenshots captured.",
    recommendations: {
      strong_hire: "Strong Hire",
      hire: "Hire",
      maybe: "Maybe",
      no_hire: "No Hire",
      strong_no_hire: "Strong No Hire",
    } as Record<string, string>,
  },
  notifications: {
    title: "Notifications",
    empty: "No notifications yet.",
  },
  consent: {
    welcomeHi: "Hi {name}, welcome",
    invitedByCompany: "{company} has invited you to an AI interview{job}.",
    invitedGeneric: "You've been invited to an AI interview{job}.",
    forRole: " for {title}",
    rules: [
      { title: "Camera stays on", desc: "Your camera will be on for the whole interview and periodic snapshots are shared with the hiring team." },
      { title: "Voice identity check", desc: "You'll record a short voice sample to verify your identity." },
      { title: "Stay on this page", desc: "Do not switch tabs, minimize, or leave the browser. Doing so is recorded as a violation and may flag your interview." },
      { title: "Timed interview", desc: "The interview has {questions} questions and is time-boxed to {minutes} minutes, with a timer on each question." },
    ],
    note: "By continuing you consent to camera and microphone recording for this interview.",
    startSetup: "I understand, start setup",
  },
  identity: {
    title: "Identity verification",
    capturePrompt: "Center your face and capture a photo.",
    recordingPrompt: 'Please say: "Hi, my name is ___ and I am ready for my interview."',
    reviewPrompt: "Watch your recording. Retake if needed, or continue when you're happy with it.",
    saving: "Saving...",
    allSet: "All set!",
    capturePhoto: "Capture photo",
    keepTalking: "Keep talking...",
    recording: "Recording",
    uploading: "Uploading",
    verified: "Verified",
    retake: "Retake",
    continue: "Looks good, continue",
  },
  runner: {
    question: "Question",
    of: "of",
    left: "left",
    hr: "HR",
    role: "Role",
    required: "Required",
    skip: "Skip",
    next: "Next",
    finish: "Finish",
    typeAnswer: "Type your answer...",
    selectOption: "Select an option...",
    selectAll: "Select all that apply.",
    yes: "Yes",
    no: "No",
  },
  categories: {
    experience: "Experience & background",
    technical: "Technical skills",
    problem_solving: "Problem solving",
    behavioral: "Behavioral & teamwork",
    situational: "Situational judgement",
    motivation: "Motivation & commitment",
    logistics: "Logistics",
    wellbeing: "Wellbeing & work style",
    role: "Role",
    hr: "HR",
  } as Record<string, string>,
  client: {
    requesting: "Requesting camera & microphone...",
    allowAccess: "Please allow access when prompted.",
    preparing: "Preparing your interview...",
    preparingHint: "Building your questions — this can take up to a minute.",
    submitting: "Submitting & evaluating...",
    takesMoment: "This only takes a moment.",
    thankYou: "Thank you!",
    completeMsg: "Your interview is complete. The hiring team will review your responses and be in touch.",
    flaggedSubmitted: "Interview submitted",
    flaggedMsg: "Your interview was flagged for leaving the page multiple times. The hiring team has been notified.",
    focusViolationsNote: "Note: some focus violations were recorded.",
    somethingWrong: "Something went wrong",
    tryAgain: "Try again",
    warning: "Please stay on this page. Leaving is recorded",
    voiceWarning:
      "Hey, please come back to your interview screen. Leaving the page is recorded, so let's stay focused — you've got this!",
    dismiss: "Dismiss",
    mediaError: "Camera and microphone access are required. Please allow access and reload.",
    genericError: "Could not start the interview.",
  },
  invalid: {
    invalidTitle: "Invalid link",
    invalidBody: "This interview link is not valid. Please check the link in your email.",
    completedTitle: "Interview already completed",
    completedBody: "You have already completed this interview. Thank you!",
  },
  partners: {
    nav: "Partners",
    title: "Partners",
    subtitle: "Invite a headhunting company to run interviews for you, or manage partnerships.",
    inviteTitle: "Invite a headhunting company",
    inviteHint: "We'll email them a link to accept and start interviewing on your behalf.",
    emailLabel: "Agency contact email",
    emailPlaceholder: "recruiter@agency.com",
    sendInvite: "Send invitation",
    listTitle: "Your partnerships",
    empty: "No partnerships yet.",
    emptyDesc: "Invite a headhunting firm to run interviews and share results with you.",
    statusPending: "Pending",
    statusActive: "Active",
    statusRevoked: "Revoked",
    directionSent: "Sent by you",
    directionReceived: "Received",
    revoke: "Revoke",
    acceptTitle: "Accept partnership",
    acceptSubtitle: "{company} invited you to run interviews as their headhunting partner.",
    acceptCta: "Accept invitation",
    acceptSignIn: "Sign in to accept",
    acceptSignUp: "Create agency account",
    acceptSuccess: "Partnership activated. You can now create jobs and invite candidates for this client.",
    acceptWrongEmail: "Sign in with {email} to accept this invitation.",
    workingFor: "Working for client",
    ownCompany: "My company (no client)",
    viaAgency: "via {agency}",
    forClient: "for {client}",
    clientDecides: "Hire decisions are made by the client company.",
    inviteSent: "Invitation sent.",
    inviteLink: "Share this accept link",
    notEmailed: "Email could not be sent — share the link below.",
  },
  contact: {
    title: "Contact us",
    subtitle: "Questions, partnership ideas, or feedback — we read every message.",
    name: "Your name",
    email: "Email",
    subject: "Subject",
    message: "Message",
    submit: "Send message",
    sending: "Sending…",
    successTitle: "Message sent",
    successBody: "Thanks for reaching out. Our team will get back to you soon.",
    sendAnother: "Send another message",
    error: "Could not send your message. Please try again.",
  },
  reports: {
    title: "Admin",
    subtitle:
      "Platform overview — support inbox, visitors, users, and recent activity.",
    inbox: "Inbox",
    unread: "unread",
    empty: "No messages yet",
    emptyDesc: "When someone submits Contact us, their message will appear here.",
    selectHint: "Select a message to read it.",
    reply: "Reply by email",
    loading: "Loading…",
    loadError: "Failed to load admin data.",
    tabOverview: "Overview",
    tabSupport: "Support",
    tabActivity: "Activity",
    tabPeople: "People",
    tabCompanies: "Companies",
    tabJobs: "Jobs",
    statManagers: "Managers",
    statCandidates: "Candidates",
    statCompanies: "Companies",
    statJobs: "Jobs",
    statInterviews: "Interviews",
    statUnread: "Unread support",
    statViews: "Page views",
    statVisitors: "Unique visitors",
    last7d: "last 7 days",
    last30d: "last 30 days",
    interviewStatus: "Interviews by status",
    viewsChart: "Views (30 days)",
    topPaths: "Top paths (30 days)",
    noViewsYet: "No page views recorded yet.",
    activityEmpty: "No recent activity.",
    colName: "Name",
    colEmail: "Email",
    colCountry: "Country",
    colRole: "Role",
    colCompany: "Company",
    colJoined: "Joined",
    colType: "Type",
    colUsers: "Users",
    colJobs: "Jobs",
    colTitle: "Title",
    colStatus: "Status",
    colVisibility: "Visibility",
    colActions: "Actions",
    bannedBadge: "Banned",
    ban: "Ban",
    unban: "Unban",
    deleteUser: "Delete",
    deleteJob: "Delete",
    confirmBan: "Ban this user? They will be signed out and cannot sign in.",
    confirmUnban: "Unban this user and allow them to sign in again?",
    confirmDeleteUser:
      "Permanently delete this user? This cannot be undone.",
    confirmDeleteJob: "Permanently delete this job? This cannot be undone.",
    actionFailed: "Action failed. Please try again.",
    jobsEmpty: "No jobs yet.",
  },
  theme: {
    toDark: "Switch to dark mode",
    toLight: "Switch to light mode",
  },
  /** Stable API / UI error codes — never expose internals here. */
  errors: {
    generic: "Something went wrong. Please try again.",
    serviceUnavailable:
      "We're having trouble right now. Please try again in a moment.",
    unauthorized: "Please sign in to continue.",
    forbidden: "You don't have permission to do that.",
    notFound: "We couldn't find what you're looking for.",
    methodNotAllowed: "That action isn't supported.",
    emailPasswordRequired: "Email and password are required.",
    passwordTooShort: "Password must be at least 8 characters.",
    companyRequired: "Company name is required.",
    countryRequired: "Please select your country / region.",
    emailExists: "An account with this email already exists.",
    signupFailed: "Could not create your account. Please try again.",
    signinFailed: "Could not sign in. Please try again.",
    emailNotRegistered: "No account found for this email. Please sign up first.",
    invalidCredentials: "Invalid email or password.",
    accountBanned: "This account has been banned.",
    otpRequired: "Enter the verification code.",
    otpInvalid: "Invalid or expired code. Sign in again.",
    otpExpired: "Code expired. Request a new one.",
    otpTooMany: "Too many attempts. Sign in again.",
    otpWrong: "That code is incorrect. Check the email and try again.",
    otpVerifyFailed: "We couldn't verify that code. Please try again.",
    otpResendFailed: "Could not resend the code. Please try again.",
    otpWait: "Wait {seconds}s before resending.",
    challengeInvalid: "Session expired. Please sign in again.",
    challengeMissing: "Session expired. Please sign in again.",
    emailRequired: "Email is required.",
    requestFailed: "Could not process your request. Please try again.",
    resetFieldsRequired: "Reset link and password are required.",
    resetInvalid: "This reset link is invalid or has expired. Request a new one.",
    resetFailed: "Could not reset your password. Please try again.",
    aiFailed: "Could not generate content right now. Please try again.",
    jobSaveFailed: "Could not save the job. Please try again.",
    jobCreateFailed: "Could not create the job. Please try again.",
    listingEndRequired: "Listing end date is required.",
    listingEndPast: "End date must be today or later.",
    partnershipInvalid: "Invalid or inactive partnership.",
    cvPdfOnly: "CV must be a PDF file.",
    cvTooLarge: "CV must be 5 MB or smaller.",
    cvSaveFailed: "Could not save the CV. Please try again.",
    applyFailed: "Could not submit your application. Please try again.",
    nameEmailRequired: "Name and email are required.",
    interviewCreateFailed: "Could not create the interview. Please try again.",
    inviteEmailRequired: "A valid email is required.",
    inviteSelf: "You cannot invite yourself.",
    inviteFailed: "Could not send the invitation. Please try again.",
    inviteNotFound: "Invitation not found.",
    inviteTokenMissing: "Missing invitation token.",
    inviteAcceptFailed: "Could not accept the invitation. Please try again.",
    contactRequired: "All fields are required.",
    contactFailed: "Could not send your message. Please try again.",
    decisionForbidden:
      "Only the client company can set hire decisions for partnered interviews.",
    interviewPrepFailed:
      "Could not prepare the interview. Please try again in a moment.",
    interviewNotFound: "Interview not found.",
    interviewDone: "This interview has already been completed.",
    mediaInvalid: "Invalid upload.",
    mediaFailed: "Upload failed. Please try again.",
    audioMissing: "Audio recording is missing. Please try again.",
    transcriptionFailed: "Could not transcribe audio. Please try again.",
    inviteDuplicate: "An invitation was already sent to this email.",
    contactInvalidEmail: "Please enter a valid email address.",
    contactTooLong: "One or more fields are too long.",
    jobNotListed: "This job is no longer available.",
    partnershipNotFound: "Partnership not found.",
    networkError: "Could not reach the server. Please try again in a moment.",
  },
};

export type Dictionary = typeof en;

const ar: Dictionary = {
  common: {
    appName: "HireSensor",
    signIn: "تسجيل الدخول",
    signUp: "إنشاء حساب",
    signOut: "تسجيل الخروج",
    getStarted: "ابدأ الآن",
    back: "رجوع",
    next: "التالي",
    save: "حفظ",
    edit: "تعديل",
    delete: "حذف",
    copy: "نسخ",
    copied: "تم النسخ",
    required: "مطلوب",
    skip: "تخطّي",
    finish: "إنهاء",
    generalInterview: "مقابلة عامة",
    candidate: "المرشّح",
    language: "اللغة",
  },
  nav: {
    dashboard: "لوحة التحكم",
    jobs: "الوظائف",
    candidates: "المرشّحون",
    interviews: "المقابلات",
    partners: "الشركاء",
    reports: "الإدارة",
    careers: "الوظائف المتاحة",
    contact: "تواصل معنا",
    myInterviews: "مقابلاتي",
    myCv: "سيرتي الذاتية",
    talent: "المواهب",
  },
  landing: {
    badge: "قابل المرشّحين بذكاء مع الذكاء الاصطناعي",
    heroTitle: "وظّف الأشخاص المناسبين عبر",
    heroHighlight: "مقابلات يقودها الذكاء الاصطناعي",
    heroSubtitle:
      "أنشئ الوصف الوظيفي، وادعُ المرشّحين، وأجرِ مقابلات ذكية آمنة وخاضعة للمراقبة مع تقييم تلقائي — لتركّز فقط على الأفضل.",
    ctaStart: "ابدأ التوظيف مجانًا",
    ctaHaveAccount: "لديّ حساب",
    testimonialsTitle: "ماذا تقول فرق التوظيف",
    testimonials: [
      {
        role: "مدير توظيف",
        quote:
          "انتقلنا من جدولة جولات فرز طويلة إلى مراجعة مقابلات مُقيَّمة في نفس اليوم. القائمة المختصرة أصبحت أدق وأعدل.",
      },
      {
        role: "قائد مواهب",
        quote:
          "الأسئلة المنظمة المبنية على الدور والسيرة تضع كل مرشّح على قدم المساواة. فريقنا يركّز فقط حيث يستحق الوقت.",
      },
      {
        role: "شريك وكالة توظيف",
        quote:
          "يحصل العملاء على توصيات واضحة مدعومة بأدلة لا على انطباع عام. أصبحت خطوتنا الافتراضية لأول مقابلة.",
      },
    ],
    featuresTitle: "كل ما تحتاجه لفرز المرشّحين بسرعة",
    featuresSubtitle: "مسار مقابلات كامل وعادل وسريع مدعوم بالذكاء الاصطناعي.",
    howTitle: "كيف يعمل",
    howSubtitle: "من الوظيفة الشاغرة إلى القرار في أربع خطوات.",
    ctaBannerTitle: "جاهز لإجراء مقابلات أذكى؟",
    ctaBannerSubtitle: "أعدّ أول مقابلة ذكية خلال دقائق.",
    footer: "مصمَّم لفرق التوظيف الحديثة.",
    features: [
      { title: "أوصاف وظيفية بالذكاء الاصطناعي", desc: "أجب عن أسئلة قصيرة واحصل على وصف وظيفي احترافي وشامل خلال ثوانٍ." },
      { title: "مقابلات خاضعة للمراقبة", desc: "الكاميرا والتحقق الصوتي وتتبّع التركيز يضمن نزاهة كل مقابلة." },
      { title: "مراقبة لمنع الغش", desc: "نكتشف تبديل التبويبات ونعلّم السلوك المريب تلقائيًا." },
      { title: "موقوتة ومنظّمة", desc: "أسئلة متسلسلة بمؤقّت لكل سؤال وحدّ أقصى 30 دقيقة." },
      { title: "تقييم بالذكاء الاصطناعي", desc: "يُقيَّم كل جواب مع نقاط القوة والملاحظات وتوصية بالتوظيف." },
      { title: "إشراف مباشر", desc: "شاهد الجلسات مباشرة واستلم إشعارات عند بدء المرشّح أو انتهائه." },
    ],
    steps: [
      { title: "أنشئ الوظيفة", desc: "استخدم المعالج الذكي لإنشاء الوصف الوظيفي." },
      { title: "ادعُ المرشّح", desc: "ارفع سيرته الذاتية وأرسل رابط مقابلة بنقرة واحدة." },
      { title: "الذكاء الاصطناعي يجري المقابلة", desc: "يكمل المرشّح مقابلة موقوتة وخاضعة للمراقبة مبنية على الوظيفة والسيرة الذاتية." },
      { title: "راجع النتائج", desc: "احصل على الدرجات والتسجيلات وتوصية واضحة." },
    ],
  },
  auth: {
    welcomeBack: "مرحبًا بعودتك",
    signInSubtitle: "سجّل الدخول للمتابعة.",
    createTitle: "أنشئ حسابك",
    createSubtitle: "اختر كيف تريد استخدام HireSensor.",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    fullName: "الاسم الكامل",
    country: "الدولة / المنطقة",
    countryPh: "اختر دولتك",
    company: "الشركة",
    workEmail: "بريد العمل",
    createAccount: "إنشاء حساب",
    noAccount: "لا تملك حسابًا؟",
    alreadyHave: "لديك حساب بالفعل؟",
    otpTitle: "تحقق من بريدك",
    otpSubtitle: "أرسلنا رمزًا مكوّنًا من 6 أرقام إلى {email}.",
    otpLabel: "رمز التحقق",
    otpVerify: "تأكيد وتسجيل الدخول",
    otpResend: "إعادة إرسال الرمز",
    otpBack: "العودة لتسجيل الدخول",
    otpInvalid: "رمز غير صالح أو منتهٍ.",
    otpResent: "تم إرسال رمز جديد إلى بريدك.",
    otpResendFailed: "تعذّر إعادة إرسال الرمز.",
    otpDevHint: "تعذّر إرسال البريد الآن. رمز التحقق هو {code}",
    otpEmailFailed:
      "تعذّر إرسال بريد التحقق. انتظر لحظة ثم اضغط «إعادة إرسال الرمز».",
    otpSent: "تم إرسال الرمز إلى بريدك.",
    agencyToggle: "أنا وكالة توظيف",
    roleHire: "أريد التوظيف",
    roleHireDesc: "مساحة عمل للوظائف والمقابلات",
    roleSeek: "أبحث عن وظيفة",
    roleSeekDesc: "تصفّح الوظائف العامة وقدّم طلبك",
    staySignedIn: "البقاء مسجّل الدخول",
    emailNotRegistered:
      "لا يوجد حساب مرتبط بهذا البريد. يرجى إنشاء حساب أولًا.",
    forgotPassword: "نسيت كلمة المرور؟",
    forgotTitle: "إعادة تعيين كلمة المرور",
    forgotSubtitle:
      "أدخل بريدك وسنرسل رابطًا لإعادة تعيين كلمة المرور.",
    forgotSubmit: "إرسال رابط إعادة التعيين",
    forgotSuccess:
      "إذا وُجد حساب بهذا البريد، فقد أرسلنا رابط إعادة التعيين. تحقق من صندوق الوارد.",
    forgotDevHint: "تعذّر إرسال البريد الآن. استخدم رابط إعادة التعيين: {url}",
    forgotBack: "العودة لتسجيل الدخول",
    resetTitle: "اختر كلمة مرور جديدة",
    resetSubtitle: "أدخل كلمة مرور جديدة لحسابك.",
    resetPassword: "كلمة المرور الجديدة",
    resetConfirm: "تأكيد كلمة المرور",
    resetSubmit: "تحديث كلمة المرور",
    resetSuccess: "تم تحديث كلمة المرور. يمكنك تسجيل الدخول الآن.",
    resetMismatch: "كلمتا المرور غير متطابقتين.",
    resetTooShort: "يجب أن تكون كلمة المرور 8 أحرف على الأقل.",
    resetInvalid:
      "رابط إعادة التعيين غير صالح أو منتهٍ. اطلب رابطًا جديدًا.",
    resetMissingToken: "رمز إعادة التعيين مفقود. افتح الرابط من بريدك.",
    cvOptional: "السيرة الذاتية (اختياري)",
    cvHint: "PDF فقط، حتى 5 ميجابايت. يمكن للشركات اكتشافك إذا جعلتها عامة.",
    cvVisibility: "ظهور السيرة الذاتية",
    cvPrivate: "خاص — أنت فقط",
    cvPublic: "عام — ظاهر للشركات ووكالات التوظيف",
  },
  profile: {
    title: "سيرتي الذاتية",
    subtitle: "ارفع سيرتك الذاتية واختر من يمكنه رؤيتها.",
    upload: "رفع سيرة ذاتية (PDF)",
    replace: "استبدال السيرة الذاتية",
    current: "السيرة الحالية",
    download: "تنزيل السيرة",
    noCv: "لم تُرفع سيرة ذاتية بعد.",
    visibility: "الظهور",
    private: "خاص — مخفي عن الشركات",
    public: "عام — يمكن للشركات والوكالات إيجادك",
    save: "حفظ التغييرات",
    saved: "تم حفظ الملف الشخصي.",
    saveError: "تعذّر حفظ الملف الشخصي.",
    loadError: "تعذّر تحميل الملف الشخصي.",
    pdfOnly: "يرجى رفع ملف PDF (بحد أقصى 5 ميجابايت).",
  },
  talent: {
    title: "المواهب",
    subtitle: "تصفّح المرشّحين الذين جعلوا سيرتهم عامة.",
    search: "ابحث بالاسم أو البريد",
    empty: "لا توجد ملفات عامة بعد.",
    emptyDesc: "عندما ينشر المرشّحون سيرتهم، ستظهر هنا.",
    view: "عرض",
    hasCv: "سيرة متاحة",
    noCv: "لا يوجد ملف سيرة",
    updated: "آخر تحديث",
    back: "العودة للمواهب",
    detailTitle: "ملف المرشّح",
    invite: "دعوة لمقابلة",
    loadError: "تعذّر تحميل ملفات المواهب.",
    notFound: "الملف غير موجود أو خاص.",
  },
  dashboard: {
    welcome: "مرحبًا",
    subtitle: "إليك نظرة سريعة على مسار التوظيف لديك.",
    newJob: "وظيفة جديدة",
    inviteCandidate: "دعوة مرشّح",
    statJobs: "الوظائف",
    statCandidates: "المرشّحون",
    statInterviews: "المقابلات",
    statCompleted: "المكتملة",
    recentInterviews: "أحدث المقابلات",
    results: "النتائج",
    viewAll: "عرض الكل",
    noInterviews: "لا توجد مقابلات بعد.",
    noMatches: "لا توجد مقابلات مطابقة للتصفية.",
    inviteFirst: "ادعُ أول مرشّح لك",
  },
  filters: {
    allJobs: "كل الوظائف",
    generalOnly: "عامة (بدون دور)",
    allStatuses: "كل الحالات",
    allDecisions: "كل القرارات",
    from: "من",
    to: "إلى",
    clear: "مسح",
    searchPlaceholder: "ابحث بالاسم أو البريد أو الوظيفة",
  },
  jobs: {
    title: "الوظائف",
    subtitle: "الأدوار التي توظّف لها.",
    newJob: "وظيفة جديدة",
    empty: "لا توجد وظائف بعد",
    emptyDesc: "أنشئ أول وصف وظيفي بالذكاء الاصطناعي.",
    createdPrefix: "أُنشئت",
    newTitle: "أنشئ وظيفة بالذكاء الاصطناعي",
    newSubtitle: "أجب عن بعض الأسئلة وسنكتب الوصف نيابةً عنك.",
    backToJobs: "العودة إلى الوظائف",
    wizardSteps: [
      "الأساسيات",
      "المسؤوليات",
      "المهارات",
      "الراتب",
      "النشر",
    ],
    jobTitle: "المسمّى الوظيفي",
    jobTitlePh: "مثال: مهندس واجهات أمامية أول",
    seniority: "المستوى",
    questionDifficulty: "صعوبة المقابلة",
    questionDifficultyHint:
      "بسيط: 20 سؤالًا · متوسط: 40 سؤالًا · متقدم: 60 سؤالًا",
    difficultyEasy: "بسيط (20 سؤالًا)",
    difficultyMedium: "متوسط (40 سؤالًا)",
    difficultyHard: "متقدم (60 سؤالًا)",
    employmentType: "نوع التوظيف",
    location: "الموقع",
    locationPh: "اكتب عن بُعد أو هجين أو مدينة…",
    locationNoMatches: "لا توجد مواقع مطابقة",
    locationPickHint: "اختر موقعًا من الاقتراحات.",
    locationHybridCity: "موقع المكتب",
    locationHybridCityPh: "اكتب مدينة للعمل الهجين…",
    locationHybridHint: "العمل الهجين يحتاج اختيار مدينة من القائمة.",
    responsibilities: "المسؤوليات الرئيسية",
    responsibilitiesPh: "ما الذي سيقوم به يوميًا؟ يمكنك استخدام نقاط.",
    mustHave: "المهارات الأساسية المطلوبة",
    mustHavePh: "مثال: React، TypeScript، خبرة 5 سنوات فأكثر",
    niceToHave: "مهارات مفضّلة",
    niceToHavePh: "مثال: Next.js، أنظمة التصميم، GraphQL",
    salaryRange: "نطاق الراتب (اختياري)",
    salaryRangePh: "مثال: 120–180 ألف ريال",
    anythingElse: "أي شيء آخر؟",
    anythingElsePh: "الفريق، الرسالة، المزايا، أسلوب العمل...",
    listingEndDate: "تاريخ انتهاء الإعلان",
    listingEndDateHint: "مطلوب. تُزال الوظيفة من الوظائف المتاحة بعد هذا التاريخ.",
    listingVisibility: "الظهور",
    visibilityPrivate: "خاص — بالدعوة فقط",
    visibilityPublic: "عام — يظهر في الوظائف المتاحة",
    listingExpires: "ينتهي {date}",
    listingExpired: "منتهٍ",
    badgePublic: "عام",
    badgePrivate: "خاص",
    generate: "إنشاء بالذكاء الاصطناعي",
    inviteCandidate: "دعوة مرشّح",
    deleteConfirm: "حذف هذه الوظيفة؟",
    noDescription: "لا يوجد وصف",
    genError: "فشل الإنشاء بالذكاء الاصطناعي. يرجى المحاولة بعد لحظات.",
    active: "نشطة",
    colTitle: "الوظيفة",
    colCandidates: "المرشّحون",
    colCompleted: "المكتملة",
    colCreated: "أُنشئت",
    colActions: "إجراءات",
    view: "عرض",
    edit: "تعديل",
    viewCandidates: "عرض المرشّحين",
    delete: "حذف",
    noMatches: "لا توجد وظائف مطابقة للتصفية.",
  },
  careers: {
    title: "الوظائف المتاحة",
    subtitle: "أدوار مفتوحة للتوظيف عبر HireSensor.",
    empty: "لا توجد وظائف عامة حاليًا.",
    emptyDesc: "عُد لاحقًا، أو أنشئ حسابًا لتقديم الطلب عند توفر الأدوار.",
    company: "الشركة",
    location: "الموقع",
    expires: "قدّم قبل {date}",
    apply: "قدّم طلبًا",
    applying: "جارٍ التقديم…",
    applySuccess: "تم التقديم — نبدأ مقابلتك.",
    signInToApply: "سجّل الدخول كمرشّح للتقديم",
    signUpToApply: "إنشاء حساب مرشّح",
    cvOptional: "السيرة الذاتية (PDF اختياري، بحد أقصى 5 ميجابايت)",
    backToCareers: "العودة إلى الوظائف المتاحة",
    myInterviewsTitle: "مقابلاتي",
    myInterviewsSubtitle: "مقابلات من الوظائف التي تقدّمت إليها.",
    myInterviewsEmpty: "لا توجد مقابلات بعد.",
    openInterview: "فتح المقابلة",
    remote: "الموقع غير محدد",
    searchPlaceholder: "ابحث بالمسمّى أو الشركة أو الموقع",
    locationPlaceholder: "الموقع",
    allSeniority: "كل المستويات",
    allEmployment: "كل أنواع التوظيف",
    allSalary: "كل نطاقات الراتب",
    salaryBuckets: {
      under_72: "أقل من 72 ألف ريال",
      "72_120": "72 – 120 ألف ريال",
      "120_180": "120 – 180 ألف ريال",
      "180_240": "180 – 240 ألف ريال",
      "240_360": "240 – 360 ألف ريال",
      "360_480": "360 – 480 ألف ريال",
      "480_plus": "480 ألف ريال فأكثر",
    },
    sortBy: "ترتيب حسب",
    sortNewest: "الأحدث أولاً",
    sortOldest: "الأقدم أولاً",
    sortTitleAsc: "المسمّى أ–ي",
    sortTitleDesc: "المسمّى ي–أ",
    sortCompanyAsc: "الشركة أ–ي",
    sortExpiresSoon: "الأقرب للانتهاء",
    noMatches: "لا توجد وظائف تطابق عوامل التصفية.",
    noMatchesDesc: "جرّب مسح عوامل التصفية أو توسيع البحث.",
    clearFilters: "مسح عوامل التصفية",
  },
  candidates: {
    title: "المرشّحون",
    subtitle: "الأشخاص المدعوّون للمقابلة.",
    inviteCandidate: "دعوة مرشّح",
    empty: "لا يوجد مرشّحون بعد",
    noMatches: "لا يوجد مرشّحون مطابقون للتصفية.",
    noInterview: "لم تُجرَ مقابلة بعد",
    latestInterview: "آخر مقابلة",
    colCandidate: "المرشّح",
    colJob: "الوظيفة",
    colScore: "الدرجة",
    colStatus: "الحالة",
    colDecision: "القرار",
    colInvited: "الدعوة",
    colActions: "إجراءات",
    view: "عرض",
    newTitle: "دعوة مرشّح",
    newSubtitle: "ارفع سيرته الذاتية وسنرسل له رابط المقابلة عبر البريد.",
    candidateName: "اسم المرشّح",
    candidateNamePh: "محمد أحمد",
    candidateEmail: "بريد المرشّح",
    candidateEmailPh: "name@example.com",
    jobOptional: "الوظيفة (اختياري)",
    generalOption: "مقابلة عامة (بدون دور محدّد)",
    interviewLanguage: "لغة المقابلة",
    interviewTime: "موعد المقابلة (اختياري)",
    cvLabel: "السيرة الذاتية (PDF، اختياري)",
    cvUpload: "اضغط لرفع ملف PDF",
    sendInvitation: "إرسال الدعوة",
    invitedTitle: "تمت دعوة المرشّح",
    emailedMsg: "تم إرسال بريد دعوة يحتوي على رابط المقابلة.",
    notEmailedMsg: "إرسال البريد غير مُهيّأ — شارك هذا الرابط مع المرشّح:",
    viewInterview: "عرض المقابلة",
    inviteAnother: "دعوة مرشّح آخر",
  },
  interviews: {
    title: "المقابلات",
    subtitle: "تابع وراجع كل مقابلة مرشّح.",
    empty: "لا توجد مقابلات بعد",
    emptyDesc: "ادعُ مرشّحًا للبدء.",
    noMatches: "لا توجد مقابلات مطابقة للتصفية.",
    inviteCandidate: "دعوة مرشّح",
    backToInterviews: "العودة إلى المقابلات",
    waitingTitle: "بانتظار بدء المرشّح",
    waitingScheduled: "مجدولة في",
    waitingNoSchedule: "لم يُحدَّد وقت.",
    interviewLink: "رابط المقابلة",
    searchPlaceholder: "ابحث بالاسم أو البريد أو الوظيفة",
    allStatuses: "كل الحالات",
    allDecisions: "كل القرارات",
    decisionTitle: "قرار التوظيف",
    decisionHint: "حدّث تقدّم المرشّح.",
  },
  decision: {
    none: "بدون قرار",
    shortlisted: "القائمة المختصرة",
    accepted: "مقبول",
    rejected: "مرفوض",
  } as Record<string, string>,
  status: {
    invited: "مدعوّ",
    in_progress: "جارية",
    completed: "مكتملة",
    violated: "موسومة",
    expired: "منتهية",
  },
  live: {
    liveCamera: "كاميرا مباشرة",
    answered: "تمت الإجابة",
    waitingCamera: "بانتظار الكاميرا...",
    evaluating: "انتهت المقابلة — يتم إنشاء التقرير…",
    live: "مباشر",
    violations: "مخالفة تركيز مُكتشفة.",
    activity: "النشاط",
    noActivity: "لا يوجد نشاط بعد.",
    events: {
      join: "انضمّ إلى المقابلة",
      answer: "أجاب عن سؤال",
      violation: "غادر صفحة المقابلة",
      snapshot: "لقطة كاميرا",
      voice: "تم تسجيل عيّنة صوتية",
      finish: "أنهى المقابلة",
      blur: "فقدت النافذة التركيز",
    } as Record<string, string>,
  },
  results: {
    summaryNone: "لا يوجد ملخّص.",
    evaluating: "جارٍ التقييم — سيتم تحديث هذه الصفحة تلقائيًا عند جاهزية التقرير.",
    strengths: "نقاط القوة",
    concerns: "الملاحظات",
    noneNoted: "لا شيء مُسجّل.",
    identityVerification: "التحقّق من الهوية",
    voiceSample: "تسجيل التعريف",
    noVoice: "لا يوجد تسجيل تعريف.",
    answersScores: "الإجابات والدرجات",
    answerLabel: "الإجابة:",
    skipped: "تم التخطّي",
    noAnswer: "بدون إجابة",
    monitoringSnapshots: "لقطات المراقبة",
    integrityTitle: "مراقبة التبويب",
    integrityLeft: "غادر تبويب المقابلة",
    integrityTimes: "{count} مرة",
    integrityNone: "لم يُسجَّل مغادرة للتبويب.",
    integrityShots: "لقطات عند المغادرة",
    integrityNoShots: "لا توجد لقطات عند المغادرة.",
    recommendations: {
      strong_hire: "توظيف بقوة",
      hire: "توظيف",
      maybe: "ربما",
      no_hire: "عدم التوظيف",
      strong_no_hire: "عدم التوظيف بقوة",
    } as Record<string, string>,
  },
  notifications: {
    title: "الإشعارات",
    empty: "لا توجد إشعارات بعد.",
  },
  consent: {
    welcomeHi: "مرحبًا {name}",
    invitedByCompany: "دعتك {company} إلى مقابلة ذكية{job}.",
    invitedGeneric: "تمت دعوتك إلى مقابلة ذكية{job}.",
    forRole: " لوظيفة {title}",
    rules: [
      { title: "الكاميرا تبقى مفتوحة", desc: "ستكون الكاميرا مفتوحة طوال المقابلة وتُشارَك لقطات دورية مع فريق التوظيف." },
      { title: "التحقّق الصوتي من الهوية", desc: "ستسجّل عيّنة صوتية قصيرة للتحقّق من هويتك." },
      { title: "ابقَ في هذه الصفحة", desc: "لا تبدّل التبويبات أو تصغّر النافذة أو تغادر المتصفّح. يُسجَّل ذلك كمخالفة وقد يوسم مقابلتك." },
      { title: "مقابلة موقوتة", desc: "المقابلة تتضمن {questions} سؤالًا ومحدّدة بـ {minutes} دقيقة، مع مؤقّت لكل سؤال." },
    ],
    note: "بالمتابعة، فإنك توافق على تسجيل الكاميرا والميكروفون لهذه المقابلة.",
    startSetup: "أوافق، ابدأ الإعداد",
  },
  identity: {
    title: "التحقّق من الهوية",
    capturePrompt: "وجّه وجهك نحو الكاميرا والتقط صورة.",
    recordingPrompt: 'قل من فضلك: "مرحبًا، اسمي ___ وأنا مستعد لمقابلتي."',
    reviewPrompt: "شاهد تسجيلك. أعد التصوير إن لزم، أو تابع عندما تكون راضيًا عنه.",
    saving: "جارٍ الحفظ...",
    allSet: "تم بنجاح!",
    capturePhoto: "التقاط صورة",
    keepTalking: "استمر في الحديث...",
    recording: "جارٍ التسجيل",
    uploading: "جارٍ الرفع",
    verified: "تم التحقّق",
    retake: "إعادة التصوير",
    continue: "جيد، المتابعة",
  },
  runner: {
    question: "سؤال",
    of: "من",
    left: "متبقٍّ",
    hr: "موارد بشرية",
    role: "الوظيفة",
    required: "مطلوب",
    skip: "تخطّي",
    next: "التالي",
    finish: "إنهاء",
    typeAnswer: "اكتب إجابتك...",
    selectOption: "اختر خيارًا...",
    selectAll: "اختر كل ما ينطبق.",
    yes: "نعم",
    no: "لا",
  },
  categories: {
    experience: "الخبرة والخلفية",
    technical: "المهارات التقنية",
    problem_solving: "حل المشكلات",
    behavioral: "السلوك والعمل الجماعي",
    situational: "التقدير الموقفي",
    motivation: "الدافعية والالتزام",
    logistics: "التفاصيل اللوجستية",
    wellbeing: "الرفاهية وأسلوب العمل",
    role: "الوظيفة",
    hr: "موارد بشرية",
  } as Record<string, string>,
  client: {
    requesting: "جارٍ طلب الكاميرا والميكروفون...",
    allowAccess: "يرجى السماح بالوصول عند الطلب.",
    preparing: "جارٍ تجهيز مقابلتك...",
    preparingHint: "نُعدّ أسئلتك — قد يستغرق ذلك حتى دقيقة.",
    submitting: "جارٍ الإرسال والتقييم...",
    takesMoment: "لن يستغرق سوى لحظات.",
    thankYou: "شكرًا لك!",
    completeMsg: "اكتملت مقابلتك. سيراجع فريق التوظيف إجاباتك ويتواصل معك.",
    flaggedSubmitted: "تم إرسال المقابلة",
    flaggedMsg: "تم وسم مقابلتك بسبب مغادرة الصفحة عدة مرات. تم إبلاغ فريق التوظيف.",
    focusViolationsNote: "ملاحظة: تم تسجيل بعض مخالفات التركيز.",
    somethingWrong: "حدث خطأ ما",
    tryAgain: "حاول مجددًا",
    warning: "يرجى البقاء في هذه الصفحة. تُسجَّل المغادرة",
    voiceWarning:
      "مرحبًا، من فضلك عُد إلى شاشة المقابلة. مغادرة الصفحة يتم تسجيلها، فلنبقَ مركّزين — أنت قادر على النجاح!",
    dismiss: "إغلاق",
    mediaError: "الوصول إلى الكاميرا والميكروفون مطلوب. يرجى السماح بالوصول وإعادة التحميل.",
    genericError: "تعذّر بدء المقابلة.",
  },
  invalid: {
    invalidTitle: "رابط غير صالح",
    invalidBody: "رابط المقابلة غير صالح. يرجى التحقّق من الرابط في بريدك.",
    completedTitle: "المقابلة مكتملة بالفعل",
    completedBody: "لقد أكملت هذه المقابلة بالفعل. شكرًا لك!",
  },
  partners: {
    nav: "الشركاء",
    title: "الشركاء",
    subtitle: "ادعُ شركة استقطاب لإجراء المقابلات نيابةً عنك، أو أدِر الشراكات.",
    inviteTitle: "دعوة شركة استقطاب",
    inviteHint: "سنرسل لهم رابطًا لقبول الدعوة وبدء المقابلات لصالحك.",
    emailLabel: "بريد جهة الاتصال في الوكالة",
    emailPlaceholder: "recruiter@agency.com",
    sendInvite: "إرسال الدعوة",
    listTitle: "شراكاتك",
    empty: "لا توجد شراكات بعد.",
    emptyDesc: "ادعُ شركة استقطاب لإجراء المقابلات ومشاركة النتائج معك.",
    statusPending: "قيد الانتظار",
    statusActive: "نشطة",
    statusRevoked: "ملغاة",
    directionSent: "أرسلتها أنت",
    directionReceived: "مستلمة",
    revoke: "إلغاء",
    acceptTitle: "قبول الشراكة",
    acceptSubtitle: "دعتك {company} لإجراء المقابلات كشريك استقطاب.",
    acceptCta: "قبول الدعوة",
    acceptSignIn: "سجّل الدخول للقبول",
    acceptSignUp: "إنشاء حساب وكالة",
    acceptSuccess: "تم تفعيل الشراكة. يمكنك الآن إنشاء الوظائف ودعوة المرشّحين لهذا العميل.",
    acceptWrongEmail: "سجّل الدخول باستخدام {email} لقبول هذه الدعوة.",
    workingFor: "العمل لصالح العميل",
    ownCompany: "شركتي (بدون عميل)",
    viaAgency: "عبر {agency}",
    forClient: "لصالح {client}",
    clientDecides: "قرارات التوظيف يتخذها العميل.",
    inviteSent: "تم إرسال الدعوة.",
    inviteLink: "شارك رابط القبول",
    notEmailed: "تعذّر إرسال البريد — شارك الرابط أدناه.",
  },
  contact: {
    title: "تواصل معنا",
    subtitle: "أسئلة أو أفكار شراكة أو ملاحظات — نقرأ كل رسالة.",
    name: "اسمك",
    email: "البريد الإلكتروني",
    subject: "الموضوع",
    message: "الرسالة",
    submit: "إرسال الرسالة",
    sending: "جاري الإرسال…",
    successTitle: "تم إرسال الرسالة",
    successBody: "شكرًا لتواصلك. سيتواصل معك فريقنا قريبًا.",
    sendAnother: "إرسال رسالة أخرى",
    error: "تعذّر إرسال رسالتك. حاول مرة أخرى.",
  },
  reports: {
    title: "الإدارة",
    subtitle:
      "نظرة عامة على المنصة — دعم العملاء، الزوّار، المستخدمون، والنشاط الأخير.",
    inbox: "الوارد",
    unread: "غير مقروء",
    empty: "لا توجد رسائل بعد",
    emptyDesc: "عند إرسال شخص لنموذج التواصل، ستظهر رسالته هنا.",
    selectHint: "اختر رسالة لقراءتها.",
    reply: "الرد عبر البريد",
    loading: "جاري التحميل…",
    loadError: "تعذّر تحميل بيانات الإدارة.",
    tabOverview: "نظرة عامة",
    tabSupport: "الدعم",
    tabActivity: "النشاط",
    tabPeople: "الأشخاص",
    tabCompanies: "الشركات",
    tabJobs: "الوظائف",
    statManagers: "المديرون",
    statCandidates: "المرشّحون",
    statCompanies: "الشركات",
    statJobs: "الوظائف",
    statInterviews: "المقابلات",
    statUnread: "دعم غير مقروء",
    statViews: "مشاهدات الصفحات",
    statVisitors: "زوّار فريدون",
    last7d: "آخر 7 أيام",
    last30d: "آخر 30 يومًا",
    interviewStatus: "المقابلات حسب الحالة",
    viewsChart: "المشاهدات (30 يومًا)",
    topPaths: "أكثر المسارات (30 يومًا)",
    noViewsYet: "لا توجد مشاهدات مسجّلة بعد.",
    activityEmpty: "لا يوجد نشاط حديث.",
    colName: "الاسم",
    colEmail: "البريد",
    colCountry: "الدولة",
    colRole: "الدور",
    colCompany: "الشركة",
    colJoined: "تاريخ الانضمام",
    colType: "النوع",
    colUsers: "المستخدمون",
    colJobs: "الوظائف",
    colTitle: "العنوان",
    colStatus: "الحالة",
    colVisibility: "الظهور",
    colActions: "إجراءات",
    bannedBadge: "محظور",
    ban: "حظر",
    unban: "رفع الحظر",
    deleteUser: "حذف",
    deleteJob: "حذف",
    confirmBan: "حظر هذا المستخدم؟ سيتم تسجيل خروجه ولن يتمكن من تسجيل الدخول.",
    confirmUnban: "رفع الحظر عن هذا المستخدم والسماح له بتسجيل الدخول مجددًا؟",
    confirmDeleteUser: "حذف هذا المستخدم نهائيًا؟ لا يمكن التراجع عن ذلك.",
    confirmDeleteJob: "حذف هذه الوظيفة نهائيًا؟ لا يمكن التراجع عن ذلك.",
    actionFailed: "فشلت العملية. حاول مرة أخرى.",
    jobsEmpty: "لا توجد وظائف بعد.",
  },
  theme: {
    toDark: "التبديل إلى الوضع الداكن",
    toLight: "التبديل إلى الوضع الفاتح",
  },
  errors: {
    generic: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    serviceUnavailable:
      "نواجه مشكلة مؤقتة الآن. يرجى المحاولة بعد لحظات.",
    unauthorized: "يرجى تسجيل الدخول للمتابعة.",
    forbidden: "ليس لديك صلاحية للقيام بذلك.",
    notFound: "تعذّر العثور على ما تبحث عنه.",
    methodNotAllowed: "هذا الإجراء غير مدعوم.",
    emailPasswordRequired: "البريد الإلكتروني وكلمة المرور مطلوبان.",
    passwordTooShort: "يجب أن تكون كلمة المرور 8 أحرف على الأقل.",
    companyRequired: "اسم الشركة مطلوب.",
    countryRequired: "يرجى اختيار الدولة / المنطقة.",
    emailExists: "يوجد حساب مرتبط بهذا البريد بالفعل.",
    signupFailed: "تعذّر إنشاء حسابك. يرجى المحاولة مرة أخرى.",
    signinFailed: "تعذّر تسجيل الدخول. يرجى المحاولة مرة أخرى.",
    emailNotRegistered:
      "لا يوجد حساب مرتبط بهذا البريد. يرجى إنشاء حساب أولًا.",
    invalidCredentials: "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
    accountBanned: "تم حظر هذا الحساب.",
    otpRequired: "أدخل رمز التحقق.",
    otpInvalid: "رمز غير صالح أو منتهٍ. سجّل الدخول مجددًا.",
    otpExpired: "انتهت صلاحية الرمز. اطلب رمزًا جديدًا.",
    otpTooMany: "محاولات كثيرة. سجّل الدخول مجددًا.",
    otpWrong: "الرمز غير صحيح. تحقّق من البريد وحاول مجددًا.",
    otpVerifyFailed: "تعذّر التحقق من الرمز. يرجى المحاولة مرة أخرى.",
    otpResendFailed: "تعذّر إعادة إرسال الرمز. يرجى المحاولة مرة أخرى.",
    otpWait: "انتظر {seconds} ثانية قبل إعادة الإرسال.",
    challengeInvalid: "انتهت الجلسة. يرجى تسجيل الدخول مجددًا.",
    challengeMissing: "انتهت الجلسة. يرجى تسجيل الدخول مجددًا.",
    emailRequired: "البريد الإلكتروني مطلوب.",
    requestFailed: "تعذّر معالجة طلبك. يرجى المحاولة مرة أخرى.",
    resetFieldsRequired: "رابط إعادة التعيين وكلمة المرور مطلوبان.",
    resetInvalid:
      "رابط إعادة التعيين غير صالح أو منتهٍ. اطلب رابطًا جديدًا.",
    resetFailed: "تعذّر إعادة تعيين كلمة المرور. يرجى المحاولة مرة أخرى.",
    aiFailed: "تعذّر إنشاء المحتوى الآن. يرجى المحاولة مرة أخرى.",
    jobSaveFailed: "تعذّر حفظ الوظيفة. يرجى المحاولة مرة أخرى.",
    jobCreateFailed: "تعذّر إنشاء الوظيفة. يرجى المحاولة مرة أخرى.",
    listingEndRequired: "تاريخ انتهاء الإدراج مطلوب.",
    listingEndPast: "يجب أن يكون تاريخ الانتهاء اليوم أو لاحقًا.",
    partnershipInvalid: "شراكة غير صالحة أو غير نشطة.",
    cvPdfOnly: "يجب أن يكون ملف السيرة الذاتية بصيغة PDF.",
    cvTooLarge: "يجب ألا يتجاوز حجم السيرة الذاتية 5 ميغابايت.",
    cvSaveFailed: "تعذّر حفظ السيرة الذاتية. يرجى المحاولة مرة أخرى.",
    applyFailed: "تعذّر إرسال طلبك. يرجى المحاولة مرة أخرى.",
    nameEmailRequired: "الاسم والبريد الإلكتروني مطلوبان.",
    interviewCreateFailed: "تعذّر إنشاء المقابلة. يرجى المحاولة مرة أخرى.",
    inviteEmailRequired: "يلزم بريد إلكتروني صالح.",
    inviteSelf: "لا يمكنك دعوة نفسك.",
    inviteFailed: "تعذّر إرسال الدعوة. يرجى المحاولة مرة أخرى.",
    inviteNotFound: "الدعوة غير موجودة.",
    inviteTokenMissing: "رمز الدعوة مفقود.",
    inviteAcceptFailed: "تعذّر قبول الدعوة. يرجى المحاولة مرة أخرى.",
    contactRequired: "جميع الحقول مطلوبة.",
    contactFailed: "تعذّر إرسال رسالتك. يرجى المحاولة مرة أخرى.",
    decisionForbidden:
      "يمكن لشركة العميل فقط تحديد قرارات التوظيف في المقابلات المشتركة.",
    interviewPrepFailed:
      "تعذّر تجهيز المقابلة. يرجى المحاولة بعد لحظات.",
    interviewNotFound: "المقابلة غير موجودة.",
    interviewDone: "تم إكمال هذه المقابلة بالفعل.",
    mediaInvalid: "رفع غير صالح.",
    mediaFailed: "فشل الرفع. يرجى المحاولة مرة أخرى.",
    audioMissing: "تسجيل الصوت مفقود. يرجى المحاولة مرة أخرى.",
    transcriptionFailed: "تعذّر تحويل الصوت إلى نص. يرجى المحاولة مرة أخرى.",
    inviteDuplicate: "تم إرسال دعوة إلى هذا البريد بالفعل.",
    contactInvalidEmail: "يرجى إدخال بريد إلكتروني صالح.",
    contactTooLong: "واحد أو أكثر من الحقول طويل جدًا.",
    jobNotListed: "هذه الوظيفة لم تعد متاحة.",
    partnershipNotFound: "الشراكة غير موجودة.",
    networkError: "تعذّر الوصول إلى الخادم. يرجى المحاولة بعد لحظات.",
  },
};

export const dictionaries: Record<Locale, Dictionary> = { en, ar };

export function getDictionary(locale: Locale): Dictionary {
  return dictionaries[locale] ?? en;
}
